import type { AgentRunSummary } from './agent'
import type { AgentMemoryEntry } from './agentDefinition'
import type { WorkflowScheduleOrigin } from './workflowSchedule'

export type WorkflowRunStatus =
  | 'Pending'
  | 'Running'
  | 'Completed'
  | 'Failed'
  | 'Cancelled'
  | 'AwaitingApproval'
  | 'AwaitingUserInput'

export type WorkflowPhaseStatus =
  | 'Pending'
  | 'Running'
  | 'Completed'
  | 'Failed'
  | 'Cancelled'
  | 'AwaitingApproval'
  | 'AwaitingUserInput'
  | 'Skipped'

export type WorkflowNodeStatus =
  | 'Pending'
  | 'Ready'
  | 'Running'
  | 'RetryPending'
  | 'AwaitingUserInput'
  | 'Completed'
  | 'Failed'
  | 'Skipped'

export type ContainerStrategy = 'None' | 'Inherit' | 'New'

export type WorkflowTriggerSource = 'Ui' | 'Chat' | 'Api' | 'Schedule' | 'Chain'

export type LinkedEntityType = 'WorkItem' | 'Intent' | 'Project'

export type LogSource = 'system' | 'claude-code' | 'tool-call' | 'command' | 'error'

export type LogLevel = 'info' | 'warning' | 'error'

export interface RetryStrategy {
  maxRetries: number
  delaySeconds: number
  backoffMultiplier: number
  retryableExitCodes?: number[]
}

export interface WorkflowRun {
  id: string
  definitionId: string
  definitionName: string
  definitionDisplayName: string
  definitionVersion: string
  status: WorkflowRunStatus
  triggeredBy: string
  triggeredByEmail: string | null
  triggeredByUserId: string | null
  triggeredFrom: WorkflowTriggerSource
  inputValues: Record<string, unknown> | null
  projectId: string
  linkedEntityType: LinkedEntityType | null
  linkedEntityId: string | null
  linkedEntityNumber: number | null
  linkedEntityAvailable?: boolean | null
  linkedEntityStatus?: string | null
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  durationMs: number | null
  leadTimeMs: number | null
  currentPhaseName: string | null
  pendingApprovalPhaseId: string | null
  pendingApprovalRoles: string[] | null
  currentNodeName: string | null
  totalPhases: number
  completedPhases: number
  errorSummary: string | null
  chatId: string | null
  chainRunId: string | null
  chainDisplayName: string | null
  chainLinkName: string | null
  chainLinkPosition: number | null
  chainLinksCount: number | null
  scheduleOrigin?: WorkflowScheduleOrigin | null
}

export interface WorkflowRunDetail extends WorkflowRun {
  phases: WorkflowRunPhase[]
  approvalRequests: WorkflowRunApprovalRequest[]
  agentSummary?: AgentRunSummary
  totalTokens?: number
  currentSpendUsd: number | null
  capUsd: number | null
  lastEventId: string | null
}

export interface WorkflowRunPhase {
  id: string
  runId: string
  name: string
  order: number
  status: WorkflowPhaseStatus
  requiresApproval: boolean
  approvalMessage: string | null
  approvalRoles: string[] | null
  startedAt: string | null
  completedAt: string | null
  branches: WorkflowRunBranch[]
}

export interface WorkflowRunBranch {
  id: string
  phaseId: string
  name: string
  order: number
  isParallel: boolean
  nodes: WorkflowRunNode[]
}

export interface WorkflowRunNode {
  id: string
  branchId: string
  name: string
  taskType: string
  order: number
  feedbackEntry: boolean
  status: WorkflowNodeStatus
  containerStrategy: ContainerStrategy
  containerId: string | null
  imageUri: string | null
  retryStrategy: RetryStrategy | null
  retryCount: number
  manualRetryCount: number
  maxRetries: number
  nextRetryAt: string | null
  startedAt: string | null
  completedAt: string | null
  durationMs: number | null
  errorMessage: string | null
  errorCode: string | null
  connectorType: string | null
  resolvedModel?: string
  resolvedEffort?: string
  defaultModel?: string
  defaultEffort?: string
  defaultSource?: 'workflow' | 'agent'
  promptSource?: NodePromptSource
  promptId?: string | null
  promptName?: string | null
  promptAvailable?: boolean
  agent?: WorkflowRunNodeAgent
}

export interface WorkflowRunNodeContent {
  nodeId: string
  inputValues: Record<string, unknown> | null
  resolvedPrompt: string | null
  outputs: WorkflowNodeOutput[]
}

export interface WorkflowRunNodeAgent {
  agentId: string
  agentVersionId: string
  name: string
  displayName: string
  version: string
}

export interface WorkflowRunNodeMemory {
  isAgentNode: boolean
  memoryEnabled: boolean
  entries: AgentMemoryEntry[]
}

export type NodePromptSourceKind = 'node-override'

export interface NodePromptSource {
  source: NodePromptSourceKind
  repository?: string
  branch?: string
  filePath?: string
}

export interface WorkflowNodeOutput {
  id: string
  nodeId: string
  key: string
  value: string
  isArtifact: boolean
}

export interface WorkflowRunApprovalRequest {
  id: string
  runId: string
  phaseId: string
  phaseName: string
  message: string
  status: 'Pending' | 'Approved' | 'Rejected' | 'Superseded'
  decidedBy: string | null
  decidedAt: string | null
  comment: string | null
  approvalRoles: string[] | null
}

export interface NodeExecutionLogEntry {
  timestamp: string
  source: LogSource
  level: LogLevel
  message: string
  nodeId: string
}

export type WorkflowRunEventType =
  | 'RunStarted'
  | 'RunCompleted'
  | 'RunFailed'
  | 'RunCancelled'
  | 'PhaseStarted'
  | 'PhaseCompleted'
  | 'PhaseFailed'
  | 'PhaseAwaitingApproval'
  | 'NodeReady'
  | 'NodeStarted'
  | 'NodeCompleted'
  | 'NodeFailed'
  | 'NodeRetryPending'
  | 'NodeSkipped'
  | 'LogEntry'

export interface WorkflowRunEvent {
  type: WorkflowRunEventType
  runId: string
  timestamp: string
  data: Record<string, unknown>
}

export interface TriggerWorkflowRequest {
  definitionName: string
  inputValues: Record<string, unknown>
}

export interface TriggerWorkflowResponse {
  runId: string
  chatId: string
}

export interface WorkflowRunFilters {
  search?: string
  statuses?: WorkflowRunStatus[]
  definitionName?: string
  fromDate?: string
  toDate?: string
  sortBy?: WorkflowRunSortColumn
  sortDirection?: 'asc' | 'desc'
}

export function isWorkflowRunDetail(run: WorkflowRun | WorkflowRunDetail): run is WorkflowRunDetail {
  return 'phases' in run
}

export type WorkflowRunSortColumn = 'title' | 'linkedItem' | 'status' | 'phases' | 'started' | 'duration' | 'leadTime' | 'user'

export const WORKFLOW_RUN_DEFAULT_SORT_DIRECTIONS: Record<WorkflowRunSortColumn, 'asc' | 'desc'> = {
  title: 'asc',
  linkedItem: 'asc',
  status: 'asc',
  phases: 'asc',
  started: 'desc',
  duration: 'desc',
  leadTime: 'desc',
  user: 'asc',
}

export function getPhaseEffectiveStatus(phase: WorkflowRunPhase): WorkflowPhaseStatus {
  if (phase.status === 'Running') {
    const hasAwaitingInput = phase.branches.some(b =>
      b.nodes.some(n => n.status === 'AwaitingUserInput')
    )
    if (hasAwaitingInput) return 'AwaitingUserInput'
  }
  return phase.status
}

const ACTIVE_RUN_STATUSES: Set<WorkflowRunStatus> = new Set(['Pending', 'Running', 'AwaitingApproval', 'AwaitingUserInput'])

export function isActiveRunStatus(status: WorkflowRunStatus): boolean {
  return ACTIVE_RUN_STATUSES.has(status)
}

const CANCELLABLE_RUN_STATUSES: Set<WorkflowRunStatus> = new Set(['Pending', 'Running', 'AwaitingApproval', 'AwaitingUserInput'])

export function isCancellableRunStatus(status: WorkflowRunStatus): boolean {
  return CANCELLABLE_RUN_STATUSES.has(status)
}

export interface WorkflowNodeLogsResponse {
  logs: string | null
  containerId: string | null
  hasLogs: boolean
  liveContainerLogsAvailable: boolean
}

export interface NodeLiveLogsSnapshot {
  containerId: string | null
  logs: string
}

export interface WorkflowNodeLogs extends WorkflowNodeLogsResponse {
  lastLiveLogs: NodeLiveLogsSnapshot | null
}

export interface WorkflowNodeErrorSummary {
  nodeId: string
  nodeName: string
  status: WorkflowNodeStatus
  errorMessage: string | null
  errorCode: string | null
  connectorType: string | null
  lastExitCode: number | null
  retryCount: number
  manualRetryCount: number
}

export interface WorkflowRunStreamEvent {
  eventType: string
  entityId: string
  status: string
  timestamp: string
  metadataJson: string | null
}
